import { getNetworkStatus } from '@/lib/network-status';
import {
  getCallDiagnostics,
  getNetworkDiagnosticsOnError,
} from '@/services/logger/call-diagnostics';
import { createLogger } from '@/services/logger/logger';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';
import { HangupReason, VonageError } from '@vonage/client-sdk';
import { useCallback, useEffect, useRef } from 'react';
import { sessionManager, useVonageClient } from '../../vonage-client-provider';
import { isNetworkFetchError, isTerminalCallStateError, serializeError } from '../error-utils';
import { trackDeviceCallFailure, useVoiceCallState } from '../voice-call-container';
import { withRetry } from '../with-retry';

const knownErrorsForHangupCall = [
  'voice:error:invalid_call_action',
  'voice:error:no_call_found',
  'voice:error:member_not_in_conversation',
];

const log = createLogger('Hangup', ['calls']);

const HANGUP_TIMEOUT_MS = 8000;
const HANGUP_SAFETY_NET_MS = 5000;

/**
 * @internal
 * This hook is used internally by VoiceCallActionsProvider.
 * Consumers should use useVoiceCallActions() instead.
 */
export const useHangup = () => {
  const [call, callDispatch] = useVoiceCallState();
  const vonageClient = useVonageClient();
  const safetyNetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callStateRef = useRef(call.callState);

  useEffect(() => {
    callStateRef.current = call.callState;
  }, [call.callState]);

  const clearSafetyNet = useCallback(() => {
    if (safetyNetTimerRef.current) {
      clearTimeout(safetyNetTimerRef.current);
      safetyNetTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearSafetyNet();
  }, [clearSafetyNet]);

  useEffect(() => {
    const handleCallHangup = vonageClient.on('callHangup', (callId, _quality, reason) => {
      if (call.callId !== callId) {
        return;
      }
      clearSafetyNet();
      callDispatch({ type: 'call:hangup', reason, callId });
    });

    return () => {
      vonageClient.off('callHangup', handleCallHangup);
    };
  }, [callDispatch, call.callId, vonageClient, clearSafetyNet]);

  const hangup = useCallback(async () => {
    const callId = call.callId;
    const diagnostics = await getCallDiagnostics();

    const connectionHealth = sessionManager.getConnectionState();

    log.info('Hangup attempt', {
      callId,
      hasCallId: !!callId,
      callState: call.callState,
      connectionHealth,
      timestamp: Date.now(),
      diagnostics,
    });

    if (!callId) {
      log.error('Hangup without callId', {
        callState: call.callState,
        timestamp: Date.now(),
        diagnostics,
      });
      // If state is non-idle but callId is missing, reset to idle to prevent stuck UI
      if (call.callState !== 'idle') {
        callDispatch({ type: 'call:hangup', reason: HangupReason.LocalHangup });
      }
      return;
    }

    if (call.callState === 'reconnectable') {
      callDispatch({ type: 'call:hangup', reason: HangupReason.LocalHangup });
      return;
    }

    const handleHangupFailure = async (error: unknown) => {
      if (error instanceof VonageError) {
        if (knownErrorsForHangupCall.includes(error.code ?? '')) {
          try {
            const leg = await vonageClient.getLeg(callId);

            if (leg.status === 'completed') {
              log.info('Hangup failed', {
                callId,
                reason: 'leg_completed',
                legStatus: leg.status,
                ...serializeError(error),
                connectionHealth,
                sessionRefreshAttempted: false,
                sessionRefreshSkipReason: 'leg_already_completed',
                timestamp: Date.now(),
                diagnostics,
              });
              callDispatch({ type: 'call:hangup', reason: HangupReason.LocalHangup });
              return false;
            }
          } catch (legError) {
            log.info('Hangup failed', {
              callId,
              reason: 'terminal_call_state_error',
              ...serializeError(error),
              legError: serializeError(legError),
              connectionHealth,
              sessionRefreshAttempted: false,
              sessionRefreshSkipReason: 'call_no_longer_exists',
              timestamp: Date.now(),
              diagnostics,
            });
            callDispatch({ type: 'call:hangup', reason: HangupReason.LocalHangup });
            return false;
          }
        }
      }

      if (isNetworkFetchError(error)) {
        const failureNetwork = getNetworkStatus();
        log.error('Hangup failed', {
          callId,
          reason: 'network_fetch_error',
          ...serializeError(error),
          connectionHealth,
          sessionRefreshAttempted: false,
          sessionRefreshSkipReason: 'network_error_abort',
          timestamp: Date.now(),
          diagnostics,
          failureNetworkStatus: failureNetwork,
        });
        getNetworkDiagnosticsOnError()
          .then((enriched) => {
            log.info('Hangup failed', {
              callId,
              reason: 'network_fetch_error_enriched',
              ...serializeError(error),
              timestamp: Date.now(),
              enrichedDiagnostics: enriched,
            });
          })
          .catch(() => {});
        callDispatch({ type: 'call:hangup', reason: HangupReason.LocalHangup });
        return false; // abort retry
      }

      if (isTerminalCallStateError(error)) {
        log.info('Hangup failed', {
          callId,
          reason: 'terminal_call_state_error',
          ...serializeError(error),
          connectionHealth,
          sessionRefreshAttempted: false,
          sessionRefreshSkipReason: 'call_no_longer_exists',
          timestamp: Date.now(),
          diagnostics,
        });
        callDispatch({ type: 'call:hangup', reason: HangupReason.LocalHangup });
        return false;
      }

      // Unknown hangup errors do not trigger a session refresh.
      // `useVerifiedHangup` already covers recovery via getLeg polling,
      // server-side force-hangup, and the 30s privacy fallback — none of
      // which need a fresh session. `withRetry` will still retry the hangup
      // once; if that fails, `onFailure` below clears the UI.
      log.info('Hangup failed', {
        callId,
        reason: 'unknown_error_retrying',
        ...serializeError(error),
        connectionHealth,
        sessionRefreshAttempted: false,
        sessionRefreshSkipReason: 'unknown_error',
        timestamp: Date.now(),
      });
    };

    clearSafetyNet();

    try {
      await withRetry(
        async () => {
          callDispatch({ type: 'call:hangingup' });
          await Promise.race([
            vonageClient.hangup(callId),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('hangup_timeout')), HANGUP_TIMEOUT_MS),
            ),
          ]);
        },
        {
          name: 'attemptHangUp',
          maxRetries: 1,
          onRetry: handleHangupFailure,
          skipFailureIfHandled: true,
          sentry: {
            tag: 'voice_hangup',
            extra: {
              callId,
              ...call,
            },
          },
          onFailure: (error: unknown) => {
            log.error('Hangup failed', {
              callId,
              reason: 'max_retries_exceeded',
              ...serializeError(error),
              connectionHealth,
              sessionRefreshAttempted: false,
              sessionRefreshSkipReason: 'max_retries_exhausted',
              timestamp: Date.now(),
              diagnostics,
            });
            tracingService.trackEvent(CallEventName.VonageOperationError, CallEventLevel.Error, {
              deviceLegId: callId,
              errorMessage: error instanceof Error ? error.message : String(error),
            });
            trackDeviceCallFailure(call, CallEventName.VonageOperationError, {
              operation: 'hangup',
              reason: 'max_retries_exceeded',
              errorMessage: error instanceof Error ? error.message : String(error),
            });
            callDispatch({ type: 'call:hangup', reason: HangupReason.LocalHangup });
          },
        },
      );

      // Hangup request succeeded but state transition depends on Vonage callHangup event.
      // If that event doesn't arrive in time, force-clean the state as a safety net.
      safetyNetTimerRef.current = setTimeout(() => {
        safetyNetTimerRef.current = null;
        if (callStateRef.current === 'hangingup') {
          log.info('Hangup safety net triggered', {
            callId,
            reason: 'callHangup_event_not_received',
            timestamp: Date.now(),
          });
          callDispatch({ type: 'call:hangup', reason: HangupReason.LocalHangup });
        }
      }, HANGUP_SAFETY_NET_MS);
    } catch (error) {
      log.error('Hangup failed', {
        callId,
        reason: 'unexpected_error',
        ...serializeError(error),
        timestamp: Date.now(),
        diagnostics,
      });
      tracingService.trackEvent(CallEventName.VonageOperationError, CallEventLevel.Error, {
        deviceLegId: callId,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      trackDeviceCallFailure(call, CallEventName.VonageOperationError, {
        operation: 'hangup',
        reason: 'unexpected_error',
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      callDispatch({ type: 'call:hangup', reason: HangupReason.LocalHangup });
    }
  }, [call, callDispatch, vonageClient, clearSafetyNet]);

  return { hangup };
};
